/**
 * iOS-Safe Storage Utility
 * Handles localStorage/sessionStorage access safely on iOS Safari
 * Falls back to in-memory storage when storage is unavailable (Private Mode, etc.)
 */

// In-memory fallback storage
const memoryStorage = new Map();

/**
 * Test if storage is available
 * iOS Private Mode and some configurations throw on storage access
 */
function isStorageAvailable(type) {
  try {
    const storage = type === 'local' ? window.localStorage : window.sessionStorage;
    const testKey = '__storage_test__';
    storage.setItem(testKey, testKey);
    storage.removeItem(testKey);
    return true;
  } catch (e) {
    // QuotaExceededError, SecurityError, or other storage errors
    return false;
  }
}

// Cache availability check results
let localStorageAvailable = null;
let sessionStorageAvailable = null;

/**
 * Get item from localStorage with iOS-safe fallback
 */
export function getLocalItem(key) {
  try {
    if (localStorageAvailable === null) {
      localStorageAvailable = isStorageAvailable('local');
    }
    
    if (localStorageAvailable) {
      return localStorage.getItem(key);
    }
    
    // Fallback to memory
    return memoryStorage.get(`local_${key}`) || null;
  } catch (e) {
    console.warn('[SafeStorage] localStorage.getItem failed:', e.message);
    return memoryStorage.get(`local_${key}`) || null;
  }
}

/**
 * Set item in localStorage with iOS-safe fallback
 */
export function setLocalItem(key, value) {
  try {
    if (localStorageAvailable === null) {
      localStorageAvailable = isStorageAvailable('local');
    }
    
    if (localStorageAvailable) {
      localStorage.setItem(key, value);
      return true;
    }
    
    // Fallback to memory
    memoryStorage.set(`local_${key}`, value);
    return true;
  } catch (e) {
    console.warn('[SafeStorage] localStorage.setItem failed:', e.message);
    memoryStorage.set(`local_${key}`, value);
    return false;
  }
}

/**
 * Remove item from localStorage with iOS-safe fallback
 */
export function removeLocalItem(key) {
  try {
    if (localStorageAvailable === null) {
      localStorageAvailable = isStorageAvailable('local');
    }
    
    if (localStorageAvailable) {
      localStorage.removeItem(key);
    }
    
    // Always try to clean memory fallback too
    memoryStorage.delete(`local_${key}`);
    return true;
  } catch (e) {
    console.warn('[SafeStorage] localStorage.removeItem failed:', e.message);
    memoryStorage.delete(`local_${key}`);
    return false;
  }
}

/**
 * Get item from sessionStorage with iOS-safe fallback
 */
export function getSessionItem(key) {
  try {
    if (sessionStorageAvailable === null) {
      sessionStorageAvailable = isStorageAvailable('session');
    }
    
    if (sessionStorageAvailable) {
      return sessionStorage.getItem(key);
    }
    
    // Fallback to memory
    return memoryStorage.get(`session_${key}`) || null;
  } catch (e) {
    console.warn('[SafeStorage] sessionStorage.getItem failed:', e.message);
    return memoryStorage.get(`session_${key}`) || null;
  }
}

/**
 * Set item in sessionStorage with iOS-safe fallback
 */
export function setSessionItem(key, value) {
  try {
    if (sessionStorageAvailable === null) {
      sessionStorageAvailable = isStorageAvailable('session');
    }
    
    if (sessionStorageAvailable) {
      sessionStorage.setItem(key, value);
      return true;
    }
    
    // Fallback to memory
    memoryStorage.set(`session_${key}`, value);
    return true;
  } catch (e) {
    console.warn('[SafeStorage] sessionStorage.setItem failed:', e.message);
    memoryStorage.set(`session_${key}`, value);
    return false;
  }
}

/**
 * Remove item from sessionStorage with iOS-safe fallback
 */
export function removeSessionItem(key) {
  try {
    if (sessionStorageAvailable === null) {
      sessionStorageAvailable = isStorageAvailable('session');
    }
    
    if (sessionStorageAvailable) {
      sessionStorage.removeItem(key);
    }
    
    // Always try to clean memory fallback too
    memoryStorage.delete(`session_${key}`);
    return true;
  } catch (e) {
    console.warn('[SafeStorage] sessionStorage.removeItem failed:', e.message);
    memoryStorage.delete(`session_${key}`);
    return false;
  }
}

/**
 * Check if localStorage is available (useful for UI hints)
 */
export function isLocalStorageAvailable() {
  if (localStorageAvailable === null) {
    localStorageAvailable = isStorageAvailable('local');
  }
  return localStorageAvailable;
}

/**
 * Check if sessionStorage is available
 */
export function isSessionStorageAvailable() {
  if (sessionStorageAvailable === null) {
    sessionStorageAvailable = isStorageAvailable('session');
  }
  return sessionStorageAvailable;
}

export default {
  getLocalItem,
  setLocalItem,
  removeLocalItem,
  getSessionItem,
  setSessionItem,
  removeSessionItem,
  isLocalStorageAvailable,
  isSessionStorageAvailable,
};
